import { intlLocale } from "@ghezaa/ui";

/** An amount in the market's money, whole units with separators: `2,500,000`. The currency is in the column's header. */
export function money(value: number): string {
  const whole = Math.round(value);
  return (whole === 0 ? 0 : whole).toLocaleString(intlLocale(), { maximumFractionDigits: 0 });   // never "-0"
}

/** An amount for a table cell: empty when it rounds to nothing, so only what differs is marked. */
export function moneyCell(value: number): string {
  return Math.round(value) === 0 ? "" : money(value);
}

/** A column header that names its currency once: "Balance (EGP)". */
export function inCurrency(label: string, currency: string): string {
  return currency ? `${label} (${currency})` : label;
}

/** BC's Blocked as a person reads it, or null when the customer is not blocked. */
export function blockedWord(blocked: string): string | null {
  if (!blocked) return null;
  return blocked === "All" ? "Blocked" : `Blocked: ${blocked.toLowerCase()}`;
}

/** A share as a percentage with one decimal: `29.5%`; a dash for none. */
export function percent(share: number | null | undefined): string {
  if (share === null || share === undefined) return "–";
  return share.toLocaleString(intlLocale(), { style: "percent", maximumFractionDigits: 1 });
}

/** How far a figure moved against an earlier one: `+12%`, `−8%`; null when there is nothing to compare with. */
export function change(now: number, before: number): string | null {
  if (before <= 0) return null;
  const moved = (now - before) / before;
  const text = Math.abs(moved).toLocaleString(intlLocale(), { style: "percent", maximumFractionDigits: 0 });
  return moved >= 0 ? `+${text}` : `−${text}`;
}

/** A calendar day (`2026-10-25`, no time zone) as `25 Oct 2026`. */
export function day(iso: string | null | undefined): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(intlLocale(), { day: "numeric", month: "short", year: "numeric" });
}

/** A month (`2026-10`) as `October 2026`; `short` gives `Oct 2026`. */
export function monthName(iso: string, short = false): string {
  const [y, m] = iso.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(intlLocale(), { month: short ? "short" : "long", year: "numeric" });
}

/** A count of days as a person says it: `1 day`, `7 days`. */
export const days = (n: number) => (n === 1 ? "1 day" : `${n} days`);

/** The month (`2026-10`) of a day (`2026-10-25`). */
export const monthOf = (iso: string) => iso.slice(0, 7);

/** A month (`2026-10`) as `Oct`, for a column header beside the month it is compared with. */
export function monthShort(iso: string): string {
  const [y, m] = iso.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(intlLocale(), { month: "short" });
}

/** A large amount for a stat: `40.5M`, `870K`, `950`. Tables keep every figure (`money`). */
export function compact(value: number): string {
  return value.toLocaleString(intlLocale(), { notation: "compact", maximumFractionDigits: 1 });
}
